import { useCallback, useMemo, useState, type JSX } from 'react';
import { Divider } from '../../Divider/Divider';
import { GroupColumnMenuProps } from '../types';
import { GroupMenuWrapper } from '../styled';
import { MenuToggler } from './MenuToggler';
import { MenuList } from './MenuList';

export const GroupColumnMenu = ({
  groupName,
  columns,
  provider,
  showToggler = true,
  showMenuList = true,
  className,
  checkboxVariant = 'secondary',
}: GroupColumnMenuProps): JSX.Element => {
  const [menuOpen, setMenuOpen] = useState(true);

  // Get column names for this group
  const columnNames = useMemo(() => columns.map((col) => col.name), [columns]);

  // Check selection state for this group
  const selectedCols = useMemo(
    () => columnNames.filter((name) => provider.selectedColumns.includes(name)),
    [columnNames, provider.selectedColumns]
  );

  const noneSelected = selectedCols.length === 0;
  const allSelected = selectedCols.length === columns.length && columns.length > 0;
  const indeterminate = !noneSelected && !allSelected && columns.length > 0;

  const handleToggleMenu = useCallback(() => {
    setMenuOpen((prevOpen) => !prevOpen);
  }, []);

  const handleSelectAllNone = useCallback(() => {
    if (allSelected) {
      // Deselect all columns in this group
      const newSelection = provider.selectedColumns.filter((name) => !columnNames.includes(name));
      provider.setSelectedColumns(newSelection);
    } else {
      // Select all columns in this group (handles both none selected and indeterminate states)
      const newSelection = [
        ...provider.selectedColumns,
        ...columnNames.filter((name) => !provider.selectedColumns.includes(name)),
      ];
      provider.setSelectedColumns(newSelection);
    }
  }, [allSelected, provider, columnNames]);

  return (
    <GroupMenuWrapper className={className}>
      <MenuToggler
        name={groupName}
        open={menuOpen}
        onOpenClose={handleToggleMenu}
        checked={allSelected}
        indeterminate={indeterminate}
        onSelectAllNone={handleSelectAllNone}
        showToggler={showToggler && columns.length > 0 && showMenuList}
        checkboxVariant={checkboxVariant}
      />
      {menuOpen && showMenuList && (
        <MenuList columns={columns} provider={provider} checkboxVariant={checkboxVariant} />
      )}
      <Divider />
    </GroupMenuWrapper>
  );
};
